import { ReactNode } from 'react';
import { JobsDataProvider } from '../context/JobsContext';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useServices } from '../../services/hooks/useServices';
import { useBusinesses } from '../../service-providers/hooks/useBusinesses';

/**
 * Wrapper component that automatically provides JobsContext with required dependencies.
 * 
 * This component fetches customers, services, and businesses data using their respective
 * hooks and passes them to JobsDataProvider, eliminating the need for manual prop passing.
 * 
 * @example
 * // In App.tsx routes
 * <Route path="/jobs" element={
 *   <Layout>
 *     <CustomerDataProvider>
 *       <ServicesDataProvider>
 *         <BusinessDataProvider>
 *           <JobsProviderWrapper>
 *             <JobsPage />
 *           </JobsProviderWrapper>
 *         </BusinessDataProvider>
 *       </ServicesDataProvider>
 *     </CustomerDataProvider>
 *   </Layout>
 * } />
 */
export function JobsProviderWrapper({ children }: { children: ReactNode }) {
  const { customers, loading: customersLoading } = useCustomers();
  const { services, loading: servicesLoading } = useServices();
  const { businesses, loading: businessesLoading } = useBusinesses();

  // Show loading state while dependencies are being fetched
  const isLoading = customersLoading || servicesLoading || businessesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dependencies...</p>
        </div>
      </div>
    );
  }

  return (
    <JobsDataProvider
      customers={customers}
      services={services}
      businesses={businesses}
    >
      {children}
    </JobsDataProvider>
  );
}
